import React, { useState, useEffect, useMemo } from 'react';
import BossDialog from '../../../components/BossDialog';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import { TimePickerWithFormat } from '../../../framework/forms/helpercomponents/timepicker/TimePickerWithFormat';
import ActionButton from '../../../framework/forms/helpercomponents/buttons/ActionButton';
import CollectionSelection from '../../../framework/forms/CollectionSelection';
import ToastNotification from '../../../components/ToastNotification';
import { utility } from '../../../framework/utility/utilityProvider';
import { ENTITYNAME, TOAST_TYPES } from '../../../framework/constant/constant';
import * as API from '../../../framework/API/api';
import moment from 'moment';

const WEEK_DAYS = [
  { ID: 1, Name: 'Monday', SubName: 'Mon' },
  { ID: 2, Name: 'Tuesday', SubName: 'Tue' },
  { ID: 3, Name: 'Wednesday', SubName: 'Wed' },
  { ID: 4, Name: 'Thursday', SubName: 'Thu' },
  { ID: 5, Name: 'Friday', SubName: 'Fri' },
  { ID: 6, Name: 'Saturday', SubName: 'Sat' },
  { ID: 0, Name: 'Sunday', SubName: 'Sun' }
];

const CreateRecursiveSlotPopup = (props) => {
  // Auto-detect initial slot from clickedSlot
  const initialSlot = useMemo(() => {
    if (!props.clickedSlot) return null;
    if (props.clickedSlot.Slot && (props.clickedSlot.Slot._id || props.clickedSlot.Slot.Title)) {
      return props.clickedSlot.Slot;
    }
    return props.clickedSlot;
  }, [props.clickedSlot]);

  const [selectedSlot, setSelectedSlot] = useState(initialSlot);
  const [openSlotSelection, setOpenSlotSelection] = useState(false);
  const [recursivePatterns, setRecursivePatterns] = useState([]);
  const [overlapConflicts, setOverlapConflicts] = useState([]);
  const [showOverlapDialog, setShowOverlapDialog] = useState(false);

  useEffect(() => {
    if (initialSlot) {
      setSelectedSlot(initialSlot);
    }
  }, [initialSlot]);

  // Lock slot if pre-selected from grid or if patterns have been added
  const isPreselectedSlot = !!props.clickedSlot;
  const isSlotLocked = isPreselectedSlot || recursivePatterns.length > 0;

  const slotTitle = selectedSlot?.Slot?.Title || selectedSlot?.Slot?.Description || selectedSlot?.Title || selectedSlot?.Description || "";
  const slotDuration = selectedSlot?.Slot?.Duration || selectedSlot?.Duration || 0;
  const selectedSlotId = selectedSlot?.Slot?._id || selectedSlot?.Slot_id || selectedSlot?._id || selectedSlot?.SID;

  // Channels lookup
  const [channels, setChannels] = useState(props.channels || []);
  const initialSelectedChannels = useMemo(() => {
    const defaultChan = props.clickedSlot?.Channel || props.selectedChannel;
    if (defaultChan && (defaultChan._id || defaultChan.SID)) {
      const match = (props.channels || []).find(
        (c) => (defaultChan._id && c._id === defaultChan._id) || (defaultChan.SID != null && c.SID === defaultChan.SID)
      );
      return match ? [match] : [defaultChan];
    }
    return [];
  }, [props.clickedSlot, props.selectedChannel, props.channels]);

  const [selectedChannels, setSelectedChannels] = useState(initialSelectedChannels);

  // Load all channels if needed
  useEffect(() => {
    const loadChannels = async () => {
      try {
        const res = await API.getDataLookup(ENTITYNAME.Channel, { sort: { FullChannelName: 1 } });
        if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
          setChannels(res.data);
          if (selectedChannels.length > 0) {
            const remapped = selectedChannels.map((sc) => {
              const found = res.data.find(
                (c) => (sc._id && c._id === sc._id) || (sc.SID != null && c.SID === sc.SID)
              );
              return found || sc;
            });
            setSelectedChannels(remapped);
          }
        }
      } catch (err) {
        console.error("Error loading channels lookup:", err);
      }
    };
    loadChannels();
  }, []);

  // From and To dates
  const initialFromDate = useMemo(() => {
    if (props.selectedSlotTime) {
      return new Date(props.selectedSlotTime);
    }
    return new Date();
  }, [props.selectedSlotTime]);

  const [fromDate, setFromDate] = useState(initialFromDate);
  const [toDate, setToDate] = useState(() => {
    const d = new Date(initialFromDate);
    d.setDate(d.getDate() + 7);
    return d;
  });

  // Time
  const initialTimeStr = useMemo(() => {
    if (props.selectedSlotTime) {
      const gmtTime = new Date(props.selectedSlotTime);
      const hh = String(gmtTime.getUTCHours()).padStart(2, '0');
      const mm = String(gmtTime.getUTCMinutes()).padStart(2, '0');
      const ss = String(gmtTime.getUTCSeconds()).padStart(2, '0');
      return `${hh}:${mm}:${ss}:00`;
    }
    return "10:00:00:00";
  }, [props.selectedSlotTime]);

  const [slotTime, setSlotTime] = useState(initialTimeStr);

  // Duration
  const [customDuration, setCustomDuration] = useState(() => {
    return slotDuration > 0 ? utility.convertMilisecondsToStringWithFrames(slotDuration) : "01:00:00:00";
  });

  // Sync customDuration when slotDuration changes
  useEffect(() => {
    if (slotDuration > 0) {
      setCustomDuration(utility.convertMilisecondsToStringWithFrames(slotDuration));
    }
  }, [slotDuration]);

  // Valid Days (Mon-Sun checkboxes)
  const initialValidDays = useMemo(() => {
    if (props.selectedSlotTime) {
      const day = new Date(props.selectedSlotTime).getUTCDay();
      return [day];
    }
    return [1, 2, 3, 4, 5]; // Default Mon-Fri
  }, [props.selectedSlotTime]);

  const [validDays, setValidDays] = useState(initialValidDays);
  const [isSaving, setIsSaving] = useState(false);

  // Toggle single day
  const handleToggleDay = (dayId) => {
    setValidDays((prev) => {
      if (prev.includes(dayId)) {
        return prev.filter((d) => d !== dayId);
      }
      return [...prev, dayId];
    });
  };

  // Toggle all days
  const isAllDaysSelected = WEEK_DAYS.length === validDays.length;
  const handleToggleAllDays = () => {
    if (isAllDaysSelected) {
      setValidDays([]);
    } else {
      setValidDays(WEEK_DAYS.map((d) => d.ID));
    }
  };

  // On Slot Selected from CollectionSelection
  const onSlotSelected = (dataList) => {
    if (dataList && dataList.length > 0) {
      setSelectedSlot(dataList[0]);
    }
    setOpenSlotSelection(false);
  };

  // Add Pattern to array
  const handleAddPattern = () => {
    if (!selectedSlot || !selectedSlotId) {
      ToastNotification({
        type: TOAST_TYPES.ERROR,
        title: "Validation Error",
        message: "Please select a Slot first."
      });
      return;
    }

    if (!selectedChannels || selectedChannels.length === 0) {
      ToastNotification({
        type: TOAST_TYPES.ERROR,
        title: "Validation Error",
        message: "Please select at least one Channel."
      });
      return;
    }

    if (!fromDate || !toDate) {
      ToastNotification({
        type: TOAST_TYPES.ERROR,
        title: "Validation Error",
        message: "Please specify both From Date and To Date."
      });
      return;
    }

    if (new Date(fromDate).getTime() > new Date(toDate).getTime()) {
      ToastNotification({
        type: TOAST_TYPES.ERROR,
        title: "Validation Error",
        message: "From Date cannot be after To Date."
      });
      return;
    }

    if (!slotTime || slotTime.replace(/[_:]/g, "").length === 0) {
      ToastNotification({
        type: TOAST_TYPES.ERROR,
        title: "Validation Error",
        message: "Please provide a valid Slot Time."
      });
      return;
    }

    const parsedDuration = customDuration && customDuration.replace(/[_:]/g, "").length > 0
      ? utility.convertStringWithFramesToMilliseconds(customDuration)
      : slotDuration;

    if (!parsedDuration || parsedDuration <= 0) {
      ToastNotification({
        type: TOAST_TYPES.ERROR,
        title: "Validation Error",
        message: "Please provide a valid Duration."
      });
      return;
    }

    if (!validDays || validDays.length === 0) {
      ToastNotification({
        type: TOAST_TYPES.ERROR,
        title: "Validation Error",
        message: "Please select at least one Valid Day."
      });
      return;
    }

    // Add one pattern entry per selected channel (all bound strictly to the selected slot)
    const newEntries = selectedChannels.map((channel) => ({
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      slotId: selectedSlotId,
      slotTitle: slotTitle || "Custom Slot",
      slotDuration: parsedDuration,
      duration: parsedDuration,
      durationStr: customDuration,
      bgColor: selectedSlot?.Slot?.BgColor || selectedSlot?.BgColor || "#3b82f6",
      fgColor: selectedSlot?.Slot?.FgColor || selectedSlot?.FgColor || "#ffffff",
      channel: {
        _id: channel._id,
        SID: channel.SID,
        FullChannelName: channel.FullChannelName || channel.ChannelName || ""
      },
      fromDate: new Date(fromDate),
      toDate: new Date(toDate),
      slotTime: slotTime,
      validDays: [...validDays]
    }));

    setRecursivePatterns((prev) => [...prev, ...newEntries]);

    ToastNotification({
      type: TOAST_TYPES.SUCCESS,
      title: "Pattern Added",
      message: `Added ${newEntries.length} recursive schedule pattern(s).`
    });
  };

  // Remove pattern from list
  const handleRemovePattern = (patternId) => {
    setRecursivePatterns((prev) => prev.filter((p) => p.id !== patternId));
  };

  // Save all patterns
  const handleSave = async () => {
    let patternsToSave = [...recursivePatterns];

    // If no patterns in table, but user filled form inputs, auto-add current inputs
    if (patternsToSave.length === 0) {
      if (selectedSlot && selectedSlotId && selectedChannels.length > 0 && fromDate && toDate && slotTime && validDays.length > 0) {
        const parsedDuration = customDuration && customDuration.replace(/[_:]/g, "").length > 0
          ? utility.convertStringWithFramesToMilliseconds(customDuration)
          : slotDuration;
        patternsToSave = selectedChannels.map((channel) => ({
          slotId: selectedSlotId,
          channel: {
            _id: channel._id,
            SID: channel.SID,
            FullChannelName: channel.FullChannelName || channel.ChannelName || ""
          },
          fromDate: new Date(fromDate),
          toDate: new Date(toDate),
          slotTime: slotTime,
          validDays: [...validDays],
          duration: parsedDuration,
          durationStr: customDuration
        }));
      } else {
        ToastNotification({
          type: TOAST_TYPES.ERROR,
          title: "No Patterns",
          message: "Please add at least one recursive pattern before saving."
        });
        return;
      }
    }

    setIsSaving(true);
    try {
      const primarySlotId = selectedSlotId || patternsToSave[0]?.slotId;

      const payload = {
        Slot_id: primarySlotId,
        RecursiveSchedule: patternsToSave.map((p) => ({
          Channel: p.channel,
          StartDate: new Date(p.fromDate).setUTCHours(0, 0, 0, 0),
          EndDate: new Date(p.toDate).setUTCHours(23, 59, 59, 999),
          ValidDays: p.validDays,
          SlotTime: utility.convertStringWithFramesToMilliseconds(p.slotTime),
          Duration: p.duration
        }))
      };

      const res = await API.saveProgramScheduleSlotRecursive(payload);

      if (res && res.success) {
        ToastNotification({
          type: TOAST_TYPES.SUCCESS,
          title: "Success",
          message: res.message || "Recursive Slots planned successfully"
        });
        if (props.onSuccess) {
          props.onSuccess();
        } else if (props.onClose) {
          props.onClose();
        }
      } else if (res && res.conflicts && res.conflicts.length > 0) {
        setOverlapConflicts(res.conflicts);
        setShowOverlapDialog(true);
      } else {
        ToastNotification({
          type: TOAST_TYPES.ERROR,
          title: "Error",
          message: res?.message || "Failed to save recursive slots"
        });
      }
    } catch (error) {
      console.error("Error saving recursive slots:", error);
      ToastNotification({
        type: TOAST_TYPES.ERROR,
        title: "Error",
        message: error?.message || "An unexpected error occurred"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formatDayNames = (dayIds) => {
    if (!dayIds || dayIds.length === 0) return "None";
    if (dayIds.length === 7) return "Everyday";
    const subNames = WEEK_DAYS.filter((d) => dayIds.includes(d.ID)).map((d) => d.SubName);
    return subNames.join(", ");
  };

  return (
    <>
      <BossDialog
        title="Create Recursive Slot"
        onClose={props.onClose}
        width={"960px"}
      >
        <div style={{ padding: "10px 16px" }}>
          {/* Top Section: Target Slot Picker */}
          <div
            style={{
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              padding: "12px 16px",
              backgroundColor: "#f8fafc",
              marginBottom: "16px"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
              <label style={{ fontWeight: 600, fontSize: "13px", color: "#1e293b", margin: 0 }}>
                Slot <span style={{ color: "#ef4444" }}>*</span>
              </label>
              {isSlotLocked && (
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#475569",
                    backgroundColor: "#e2e8f0",
                    padding: "2px 8px",
                    borderRadius: "4px"
                  }}
                >
                  <i className="pi pi-lock" style={{ fontSize: "10px", marginRight: "4px" }}></i>
                  {isPreselectedSlot ? "Locked to clicked slot" : "Slot locked while patterns exist"}
                </span>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center" }}>
              <input
                type="text"
                className="form-control form-control-sm"
                value={slotTitle}
                placeholder="Select Slot to plan recursively..."
                disabled
                style={{ backgroundColor: "#ffffff", fontWeight: 500 }}
              />
              {slotDuration > 0 && (
                <span
                  style={{
                    marginLeft: "6px",
                    fontSize: "11px",
                    padding: "3px 10px",
                    borderRadius: "12px",
                    backgroundColor: "#e0f2fe",
                    color: "#0369a1",
                    fontWeight: 600,
                    whiteSpace: "nowrap"
                  }}
                >
                  {utility.convertMilisecondsToStringWithFrames(slotDuration)}
                </span>
              )}
              <ActionButton
                style={{ marginLeft: "6px", height: "31px" }}
                btnColor={"success"}
                title={isSlotLocked ? "Cannot change slot while locked" : "Select Slot"}
                name={"..."}
                disabled={isSlotLocked}
                onClick={() => {
                  if (!isSlotLocked) setOpenSlotSelection(true);
                }}
              />
              {selectedSlot && !isSlotLocked && (
                <ActionButton
                  style={{ marginLeft: "4px", height: "31px" }}
                  btnColor={"danger"}
                  title={"Clear Slot"}
                  name={""}
                  icon={"xmark"}
                  onClick={() => setSelectedSlot(null)}
                />
              )}
            </div>
            {isSlotLocked && !isPreselectedSlot && (
              <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                To switch to another slot, remove the added pattern(s) below first.
              </div>
            )}
          </div>

          {/* Form: Add Pattern */}
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "16px", backgroundColor: "#f8fafc", marginBottom: "16px" }}>
            <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "12px", color: "#1e293b" }}>
              Configure Recursive Slot Pattern
            </div>

            {/* Row 1: Channel and Duration */}
            <div className="row mb-3">
              <div className="col-8">
                <label style={{ fontWeight: 500, fontSize: "12px", marginBottom: "4px", display: "block" }}>
                  Channel *
                </label>
                <MultiSelect
                  data={channels}
                  textField="FullChannelName"
                  dataItemKey="_id"
                  value={selectedChannels}
                  onChange={(e) => setSelectedChannels(e.target.value)}
                  placeholder="Select Channel(s)..."
                  style={{ width: "100%", backgroundColor: "#ffffff" }}
                />
              </div>

              <div className="col-4">
                <label style={{ fontWeight: 500, fontSize: "12px", marginBottom: "4px", display: "block" }}>
                  Duration *
                </label>
                <TimePickerWithFormat
                  value={customDuration}
                  onChange={(e) => setCustomDuration(e.target.value)}
                  isWithFrames={true}
                />
              </div>
            </div>

            {/* Row 2: From Date, To Date, Slot Time */}
            <div className="row mb-3">
              <div className="col-4">
                <label style={{ fontWeight: 500, fontSize: "12px", marginBottom: "4px", display: "block" }}>
                  From Date *
                </label>
                <DatePicker
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  format="dd/MM/yyyy"
                  style={{ width: "100%", backgroundColor: "#ffffff" }}
                />
              </div>

              <div className="col-4">
                <label style={{ fontWeight: 500, fontSize: "12px", marginBottom: "4px", display: "block" }}>
                  To Date *
                </label>
                <DatePicker
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  format="dd/MM/yyyy"
                  style={{ width: "100%", backgroundColor: "#ffffff" }}
                />
              </div>

              <div className="col-4">
                <label style={{ fontWeight: 500, fontSize: "12px", marginBottom: "4px", display: "block" }}>
                  Slot Time *
                </label>
                <TimePickerWithFormat
                  value={slotTime}
                  onChange={(e) => setSlotTime(e.target.value)}
                  isWithFrames={true}
                />
              </div>
            </div>

            {/* Row 3: Valid Days Checkboxes */}
            <div className="row mb-3">
              <div className="col-12">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                  <label style={{ fontWeight: 500, fontSize: "12px", marginBottom: 0 }}>
                    Valid Days *
                  </label>
                  <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={handleToggleAllDays}>
                    <input
                      type="checkbox"
                      id="select-all-days"
                      checked={isAllDaysSelected}
                      onChange={handleToggleAllDays}
                      style={{ cursor: "pointer", marginRight: "5px" }}
                    />
                    <label htmlFor="select-all-days" style={{ fontSize: "11px", fontWeight: 600, color: "#2563eb", cursor: "pointer", marginBottom: 0 }}>
                      Select All
                    </label>
                  </div>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", backgroundColor: "#ffffff", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1" }}>
                  {WEEK_DAYS.map((day) => {
                    const isChecked = validDays.includes(day.ID);
                    return (
                      <label
                        key={day.ID}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          marginBottom: 0,
                          fontSize: "12px",
                          fontWeight: isChecked ? 600 : 400,
                          color: isChecked ? "#1e293b" : "#64748b"
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleDay(day.ID)}
                          style={{ marginRight: "6px", cursor: "pointer" }}
                        />
                        {day.Name}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Row 4: Add Button */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleAddPattern}
                style={{ fontWeight: 600, padding: "5px 16px" }}
              >
                <i className="pi pi-plus" style={{ marginRight: "6px" }}></i>
                Add Pattern
              </button>
            </div>
          </div>

          {/* Middle Section: Added Patterns Table */}
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", marginBottom: "16px" }}>
            <div style={{ backgroundColor: "#f1f5f9", padding: "10px 16px", fontWeight: 600, fontSize: "13px", color: "#334155", borderBottom: "1px solid #e2e8f0" }}>
              Recursive Patterns List ({recursivePatterns.length})
              {slotTitle && (
                <span style={{ fontWeight: 400, color: "#64748b", marginLeft: "8px" }}>
                  for slot: <strong style={{ color: "#1e293b" }}>{slotTitle}</strong>
                </span>
              )}
            </div>

            {recursivePatterns.length === 0 ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                No patterns added yet. Select channel(s), dates, time, and valid days, then click <strong>"Add Pattern"</strong>.
              </div>
            ) : (
              <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                <table className="table table-sm table-striped mb-0" style={{ fontSize: "12px" }}>
                  <thead style={{ backgroundColor: "#f8fafc", position: "sticky", top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ width: "40px", textAlign: "center" }}>#</th>
                      <th>Slot</th>
                      <th>Channel</th>
                      <th>From Date</th>
                      <th>To Date</th>
                      <th>Time</th>
                      <th>Duration</th>
                      <th>Valid Days</th>
                      <th style={{ width: "60px", textAlign: "center" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recursivePatterns.map((pat, idx) => (
                      <tr key={pat.id}>
                        <td style={{ textAlign: "center", color: "#64748b" }}>{idx + 1}</td>
                        <td style={{ fontWeight: 500 }}>{pat.slotTitle}</td>
                        <td>{pat.channel?.FullChannelName || "N/A"}</td>
                        <td>{moment(pat.fromDate).format("DD/MM/YYYY")}</td>
                        <td>{moment(pat.toDate).format("DD/MM/YYYY")}</td>
                        <td>{pat.slotTime}</td>
                        <td style={{ fontWeight: 500 }}>
                          {pat.durationStr || (pat.duration ? utility.convertMilisecondsToStringWithFrames(pat.duration) : "N/A")}
                        </td>
                        <td>
                          <span style={{ fontSize: "11px", backgroundColor: "#f1f5f9", padding: "2px 6px", borderRadius: "4px", color: "#475569" }}>
                            {formatDayNames(pat.validDays)}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            style={{ padding: "1px 6px", fontSize: "11px" }}
                            title="Remove pattern"
                            onClick={() => handleRemovePattern(pat.id)}
                          >
                            <i className="pi pi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Bottom Actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", paddingTop: "8px", borderTop: "1px solid #e2e8f0" }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={props.onClose}
              disabled={isSaving}
              style={{ minWidth: "80px" }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleSave}
              disabled={isSaving}
              style={{ minWidth: "120px", fontWeight: 600 }}
            >
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" style={{ marginRight: "6px" }}></span>
                  Saving...
                </>
              ) : (
                "Save & Apply"
              )}
            </button>
          </div>
        </div>
      </BossDialog>

      {/* Slot selection popup from collection */}
      {openSlotSelection && (
        <CollectionSelection
          showCreate={true}
          path={"/home/slot"}
          addButtonTitle={"Select Slot"}
          title={"Select Slot"}
          entityname={ENTITYNAME.Slot}
          closeForm={() => setOpenSlotSelection(false)}
          setDataList={onSlotSelected}
          mode={"single"}
          width={"50vw"}
        />
      )}

      {/* Overlap Conflicts Warning Dialog */}
      {showOverlapDialog && (
        <BossDialog
          title="Slot Overlap Detected"
          onClose={() => setShowOverlapDialog(false)}
          width={"860px"}
        >
          <div style={{ padding: "16px 20px" }}>
            {/* Warning Alert Card */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                backgroundColor: "#fff1f2",
                border: "1px solid #fecdd3",
                borderRadius: "8px",
                padding: "14px 16px",
                marginBottom: "16px"
              }}
            >
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "14px",
                  flexShrink: 0
                }}
              >
                <i className="pi pi-exclamation-triangle" style={{ fontSize: "20px", color: "#dc2626" }}></i>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: "14px", color: "#991b1b", marginBottom: "4px" }}>
                  Cannot Create Recursive Slots: Overlapping Custom Slots Found ({overlapConflicts.length})
                </div>
                <div style={{ fontSize: "12px", color: "#b91c1c", lineHeight: "1.5" }}>
                  The requested recursive schedule overlaps with custom slot(s) already present on the timeline.
                  To protect schedule consistency, the operation was cancelled and no slots were saved.
                  Please review the overlapping slots below and adjust your schedule times, dates, or valid days.
                </div>
              </div>
            </div>

            {/* Overlap Table */}
            <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", marginBottom: "16px" }}>
              <div
                style={{
                  backgroundColor: "#f8fafc",
                  padding: "10px 16px",
                  fontWeight: 600,
                  fontSize: "13px",
                  color: "#334155",
                  borderBottom: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <span>Conflicting Slot Details</span>
                <span
                  style={{
                    backgroundColor: "#fee2e2",
                    color: "#b91c1c",
                    fontSize: "11px",
                    fontWeight: 600,
                    padding: "2px 8px",
                    borderRadius: "10px"
                  }}
                >
                  {overlapConflicts.length} Conflict{overlapConflicts.length > 1 ? "s" : ""}
                </span>
              </div>

              <div style={{ maxHeight: "280px", overflowY: "auto" }}>
                <table className="table table-sm table-hover mb-0" style={{ fontSize: "12px" }}>
                  <thead style={{ backgroundColor: "#f1f5f9", position: "sticky", top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ width: "40px", textAlign: "center" }}>#</th>
                      <th>Channel</th>
                      <th>Date</th>
                      <th>Requested Slot Time</th>
                      <th>Already Present Slot</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overlapConflicts.map((c, idx) => (
                      <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fffbfa" }}>
                        <td style={{ textAlign: "center", color: "#64748b" }}>{idx + 1}</td>
                        <td style={{ fontWeight: 600, color: "#1e293b" }}>{c.channelName}</td>
                        <td style={{ fontWeight: 500, color: "#334155" }}>{c.date}</td>
                        <td>
                          <span
                            style={{
                              backgroundColor: "#e0f2fe",
                              color: "#0369a1",
                              fontWeight: 600,
                              fontSize: "11px",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              whiteSpace: "nowrap"
                            }}
                          >
                            <i className="pi pi-clock" style={{ fontSize: "10px", marginRight: "4px" }}></i>
                            {c.candidateTime}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ fontWeight: 600, color: "#991b1b" }}>{c.existingSlotTitle}</span>
                            <span
                              style={{
                                backgroundColor: "#fee2e2",
                                color: "#b91c1c",
                                fontWeight: 600,
                                fontSize: "11px",
                                padding: "2px 8px",
                                borderRadius: "4px",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {c.existingTime}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer Buttons */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setShowOverlapDialog(false)}
                style={{ minWidth: "140px", fontWeight: 600 }}
              >
                <i className="pi pi-arrow-left" style={{ marginRight: "6px" }}></i>
                Close & Adjust
              </button>
            </div>
          </div>
        </BossDialog>
      )}
    </>
  );
};

export default CreateRecursiveSlotPopup;
